import React, { useState, useEffect, Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckCircleIcon, XMarkIcon, UserIcon, TicketIcon, ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { v4 as uuidv4 } from "uuid";
import scannerApi from "../apis/scanner.api";

export default function ScanPreviewConfirmModal({
  isOpen,
  onClose,
  bookingId,
  bookingData,
  onOptimisticUpdate,
  onConfirmed,
  onError,
}) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [count, setCount] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState(null);

  const rollbackRef = useRef(null);
  const autoConfirmTimerRef = useRef(null);

  function reset() {
    if (autoConfirmTimerRef.current) {
      clearTimeout(autoConfirmTimerRef.current);
      autoConfirmTimerRef.current = null;
    }
    setPreviewData(null);
    setPreviewError(null);
    setCount(1);
    setConfirmError(null);
    setConfirming(false);
    setLoadingPreview(false);
    setIdempotencyKey(null);
    rollbackRef.current = null;
  }

  function applyPreviewFromBooking(b) {
    try {
      const qty = Number(b.quantity || 0);
      const used = Number(b.scan_count || 0);
      const remaining = Math.max(0, qty - used);

      setPreviewData({
        booking_id: b.booking_id,
        first_name: b.first_name,
        last_name: b.last_name,
        email: b.email,
        quantity: qty,
        used,
        remaining,
        tickets: b.tickets || [],
        seat_numbers: b.seat_numbers || "{}",
        title: b.title || b.event_title || "",
        start_date_time: b.start_date_time || b.event_date || "",
        status: remaining <= 0 ? "fully_scanned" : "valid",
      });

      setCount(remaining > 0 ? remaining : 0);
      setLoadingPreview(false);
      setPreviewError(null);

      if (remaining <= 0) {
        setConfirmError("No remaining scans available for this booking.");
      } else {
        setConfirmError(null);
      }
    } catch (err) {
      console.error("applyPreviewFromBooking error:", err);
      setPreviewError("Failed to process booking data.");
      setLoadingPreview(false);
    }
  }

  async function loadPreview(bId) {
    setLoadingPreview(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      scannerApi.verifyTicket({
        payload: { qrCodeData: bId },
        success: (res) => {
          const payload = res?.data || {};
          const booking = payload?.bookingData || payload?.data || null;
          
          if (!payload.valid && payload.status === "fully_scanned") {
             // Handle fully scanned gracefully
             setPreviewData({
                status: "fully_scanned",
                remaining: 0,
                message: payload.message || "No remaining scans."
             });
             setConfirmError("No remaining scans available for this booking.");
             setLoadingPreview(false);
             return;
          }
          
          if (!payload.valid) {
             setPreviewError(payload.message || "Ticket is invalid or expired.");
             setLoadingPreview(false);
             return;
          }

          if (!booking) {
            setPreviewError("No preview data available.");
            setLoadingPreview(false);
            return;
          }

          const qty = Number(booking.quantity || 0);
          const used = Number(booking.scan_count || 0);
          const remaining = Math.max(0, qty - used);

          setPreviewData({
            booking_id: booking.booking_id,
            first_name: booking.first_name,
            last_name: booking.last_name,
            email: booking.email,
            quantity: qty,
            used,
            remaining,
            tickets: booking.tickets || [],
            seat_numbers: booking.seat_numbers || "{}",
            title: booking.title || booking.event_title || "",
            start_date_time: booking.start_date_time || booking.event_date || "",
            status: payload.status,
          });

          setCount(remaining > 0 ? remaining : 0);
          setLoadingPreview(false);

          if (remaining <= 0) {
            setConfirmError("No remaining scans available for this booking.");
          } else {
            setConfirmError(null);
          }
        },
        error: (err) => {
          console.error("scannerPreview error:", err);
          setPreviewError(
            err?.response?.data?.error ||
              err?.response?.data?.message ||
              "Failed to load booking preview."
          );
          setLoadingPreview(false);
        },
      });
    } catch (err) {
      console.error(err);
      setPreviewError("Unexpected error while loading preview.");
      setLoadingPreview(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      reset();
      return;
    }

    if (bookingData) {
      applyPreviewFromBooking(bookingData);
      return;
    }

    if (isOpen && bookingId) {
      loadPreview(bookingId);
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bookingId, bookingData]);

  useEffect(() => {
    if (!isOpen) return;
    if (!previewData) return;

    if (previewData.remaining === 1 && !confirming) {
      autoConfirmTimerRef.current = setTimeout(() => {
        if (previewData.remaining === 1 && !confirming) {
          setCount(1);
          handleConfirm();
        }
      }, 600);
    }

    return () => {
      if (autoConfirmTimerRef.current) {
        clearTimeout(autoConfirmTimerRef.current);
        autoConfirmTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, previewData]);

  const handleConfirm = async () => {
    setConfirmError(null);

    if (!previewData) return;
    if (confirming) return;

    const remainingAvailable = Number(previewData.remaining || 0);
    let requested = Number(count || 0);

    if (remainingAvailable <= 0) {
      setConfirmError("No remaining scans available for this booking.");
      return;
    }

    if (!requested || requested <= 0) {
      setConfirmError("Please specify at least one scan to confirm.");
      return;
    }

    const toScan = Math.min(requested, remainingAvailable);
    const key = uuidv4();
    setIdempotencyKey(key);

    let rollbackToken = null;
    try {
      if (typeof onOptimisticUpdate === "function") {
        rollbackToken = await onOptimisticUpdate({
          bookingId: previewData.booking_id,
          scannedNow: toScan,
        });
        rollbackRef.current = rollbackToken;
      }
    } catch (optimErr) {
      console.warn("onOptimisticUpdate threw:", optimErr);
      rollbackRef.current = null;
    }

    setConfirming(true);

    try {
      const serverRes = await new Promise((resolve, reject) => {
        scannerApi.confirmTicket({
          payload: {
            qrCodeData: previewData.booking_id,
            count: toScan,
            idempotencyKey: key,
          },
          success: (r) => resolve(r),
          error: (err) => reject(err),
        });
      });

      if (typeof onConfirmed === "function") {
        try {
          onConfirmed({
            bookingId: previewData.booking_id,
            scannedNow: toScan,
            serverResponse: serverRes,
          });
        } catch (cbErr) {
          console.warn("onConfirmed callback failed:", cbErr);
        }
      }

      setPreviewData((p) =>
        p
          ? {
              ...p,
              used: p.used + toScan,
              remaining: Math.max(0, p.remaining - toScan),
            }
          : p
      );

      setConfirming(false);
      setIdempotencyKey(null);
      onClose?.();
    } catch (err) {
      console.error("scannerConfirm error:", err);

      try {
        if (rollbackRef.current) {
          if (typeof rollbackRef.current === "function") {
            rollbackRef.current();
          } else if (
            rollbackRef.current &&
            typeof rollbackRef.current.rollback === "function"
          ) {
            rollbackRef.current.rollback();
          }
        }
      } catch (rbErr) {
        console.warn("Rollback failed:", rbErr);
      }

      const payload = err?.response?.data || {};
      const message =
        payload?.error ||
        payload?.message ||
        "Failed to confirm scan. Please try again.";
      setConfirmError(message);

      setConfirming(false);
      setIdempotencyKey(null);

      if (typeof onError === "function") onError(err);
    }
  };

  const closeAndReset = () => {
    onClose?.();
    reset();
  };

  return (
    <Transition appear show={!!isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeAndReset}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 backdrop-blur-none"
          enterTo="opacity-100 backdrop-blur-sm"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 backdrop-blur-sm"
          leaveTo="opacity-0 backdrop-blur-none"
        >
          <div className="fixed inset-0 bg-black/60 transition-all" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300 transform"
              enterFrom="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-3xl bg-white text-left shadow-2xl transition-all sm:my-8 w-full sm:max-w-lg border border-gray-100">
                {loadingPreview ? (
                  <div className="p-12 flex flex-col items-center justify-center space-y-4">
                    <div className="w-12 h-12 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin"></div>
                    <p className="text-gray-500 font-medium animate-pulse">Scanning Ticket...</p>
                  </div>
                ) : previewError ? (
                  <div className="p-8 flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                      <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
                    </div>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900 mb-2">
                      Invalid Ticket
                    </Dialog.Title>
                    <p className="text-gray-500 mb-8">{previewError}</p>
                    <button
                      type="button"
                      className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                      onClick={closeAndReset}
                    >
                      Close Scanner
                    </button>
                  </div>
                ) : !previewData ? (
                  <div className="p-8 text-center text-gray-500">No data available</div>
                ) : previewData.status === "fully_scanned" ? (
                   <div className="p-8 flex flex-col items-center text-center">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                      <InformationCircleIcon className="w-8 h-8 text-yellow-600" />
                    </div>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900 mb-2">
                      Fully Scanned
                    </Dialog.Title>
                    <p className="text-gray-500 mb-8">{previewData.message || "This booking has no remaining admissions."}</p>
                    <button
                      type="button"
                      className="w-full sm:w-auto px-8 py-3 rounded-xl font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                      onClick={closeAndReset}
                    >
                      Close Scanner
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Header */}
                    <div className="bg-emerald-500 px-6 py-6 sm:px-8 relative overflow-hidden">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-emerald-400 rounded-full opacity-50 blur-2xl"></div>
                      <div className="relative flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
                            <CheckCircleIcon className="h-8 w-8 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white drop-shadow-sm">
                              Valid Ticket
                            </h3>
                            <p className="text-emerald-50 font-medium text-sm line-clamp-1">
                              {previewData.title}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={closeAndReset}
                          className="p-2 rounded-full hover:bg-white/20 text-emerald-50 transition-colors"
                        >
                          <XMarkIcon className="h-6 w-6" />
                        </button>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="px-6 py-6 sm:px-8">
                      {/* Attendee Info */}
                      <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
                        <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                          <UserIcon className="w-6 h-6 text-blue-500" />
                        </div>
                        <div className="overflow-hidden">
                          <p className="font-bold text-gray-900 text-lg truncate">
                            {previewData.first_name} {previewData.last_name}
                          </p>
                          <p className="text-gray-500 text-sm truncate">{previewData.email}</p>
                          <p className="text-xs text-gray-400 font-mono mt-0.5 truncate">{previewData.booking_id}</p>
                        </div>
                      </div>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-3 gap-3 mb-6">
                        <div className="bg-gray-50 rounded-2xl p-3 text-center border border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Total</p>
                          <p className="text-xl font-bold text-gray-900">{previewData.quantity}</p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-3 text-center border border-gray-100">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Scanned</p>
                          <p className="text-xl font-bold text-gray-900">{previewData.used}</p>
                        </div>
                        <div className="bg-emerald-50 rounded-2xl p-3 text-center border border-emerald-100 ring-1 ring-emerald-500/10 shadow-sm">
                          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">Remaining</p>
                          <p className="text-xl font-bold text-emerald-700">{previewData.remaining}</p>
                        </div>
                      </div>

                      {/* Seats / Tickets Box */}
                      <div className="bg-gray-50 rounded-2xl p-4 mb-6 border border-gray-100 max-h-40 overflow-y-auto custom-scrollbar">
                        <div className="flex items-center gap-2 mb-3">
                          <TicketIcon className="w-5 h-5 text-gray-400" />
                          <h4 className="font-bold text-gray-700 text-sm">Booking Details</h4>
                        </div>
                        
                        {previewData.seat_numbers && previewData.seat_numbers !== "{}" ? (
                          <div className="space-y-2">
                            {Object.entries(
                              typeof previewData.seat_numbers === "string"
                                ? JSON.parse(previewData.seat_numbers || "{}")
                                : previewData.seat_numbers
                            ).map(([seat, type]) => (
                              <div key={seat} className="flex justify-between items-center bg-white px-3 py-2 rounded-xl shadow-sm border border-gray-100">
                                <span className="font-bold text-gray-900">{seat}</span>
                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-md">{type}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {(previewData.tickets || []).map((ticket, index) => (
                              <div key={index} className="flex justify-between items-center bg-white px-3 py-2 rounded-xl shadow-sm border border-gray-100">
                                <span className="font-bold text-gray-900 truncate mr-2">{ticket.ticket_name}</span>
                                <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-md whitespace-nowrap">
                                  × {ticket.quantity}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action Area */}
                      {previewData.remaining > 0 && (
                        <div className="pt-2">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1">
                              <label className="block text-sm font-bold text-gray-700 mb-2">
                                Admit Attendees
                              </label>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => setCount((c) => Math.max(1, Number(c) - 1))}
                                  disabled={confirming || count <= 1}
                                  className="w-12 h-12 flex items-center justify-center rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                >
                                  −
                                </button>
                                <div className="flex-1 text-center">
                                  <span className="text-3xl font-black text-gray-900 tabular-nums">{count}</span>
                                </div>
                                <button
                                  onClick={() => setCount((c) => Math.min(previewData.remaining, Number(c) + 1))}
                                  disabled={confirming || count >= previewData.remaining}
                                  className="w-12 h-12 flex items-center justify-center rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            
                            <div className="flex-1 sm:pl-4 sm:border-l sm:border-gray-100 mt-4 sm:mt-0">
                              <button
                                onClick={handleConfirm}
                                disabled={confirming}
                                className={`w-full h-16 rounded-2xl font-bold text-lg shadow-sm transition-all relative overflow-hidden group ${
                                  confirming
                                    ? "bg-gray-300 text-gray-500 cursor-wait"
                                    : "bg-emerald-500 text-white hover:bg-emerald-600 hover:shadow-emerald-500/25 hover:shadow-lg hover:-translate-y-0.5"
                                }`}
                              >
                                {confirming ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <div className="w-5 h-5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                                    Confirming
                                  </span>
                                ) : (
                                  <>
                                    <span className="relative z-10">Confirm {count}</span>
                                    <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          {confirmError && (
                            <p className="mt-3 text-sm font-medium text-red-500 text-center animate-bounce">
                              {confirmError}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
